import { setUfoTiming } from '../templates/ufo-adventure/domain/editorModel';
import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { draftInputSchema, type DraftInput, type DraftMedia, type GameDraft } from '../../shared/drafts';
import { ApiRequestError } from '../auth/authApi';
import {
  createStarterDraft,
  isQuestionReady,
  questionsForLevel,
  reconfigureDraft,
  removedQuestionCount,
} from './editorModel';
import { saveDraft, uploadDraftMedia } from './draftApi';
import type { TemplateKey } from '../../shared/templates';
import { templateName } from '../../shared/templates';

type TrainEditorProps = {
  initialDraft?: GameDraft;
  /** Dosyadan içe aktarılan, henüz kaydedilmemiş içerik (Milestone 033). */
  importedDraft?: DraftInput;
  mediaUploadAllowed?: boolean;
  mediaUploadMessage?: string | null;
  onCancel: () => void;
  onSaved: (draft: GameDraft) => void;
  templateKey?: TemplateKey;
};

function inputFromDraft(draft: GameDraft | undefined, templateKey: TemplateKey): DraftInput {
  if (!draft) return createStarterDraft(templateKey);
  return {
    title: draft.title,
    templateKey: draft.templateKey,
    levelMode: 'manual',
    maxLevels: draft.maxLevels,
    trackTime: draft.trackTime,
    bridgeMode: draft.bridgeMode,
    timingMode: draft.timingMode,
    ...(draft.repeatUntilFall ? { repeatUntilFall: true } : {}),
    questions: draft.questions.map((question) => ({
      prompt: question.prompt,
      wagonStyle: question.wagonStyle ?? 'wide',
      levels: question.levels,
      mediaId: question.mediaId,
      timeLimitSeconds: question.timeLimitSeconds,
      choices: question.choices.map((choice) => ({
        text: choice.text,
        isCorrect: choice.isCorrect,
        mediaId: choice.mediaId,
      })),
    })),
  };
}

export function TrainEditor({
  initialDraft,
  importedDraft,
  mediaUploadAllowed = true,
  mediaUploadMessage = null,
  onCancel,
  onSaved,
  templateKey = 'train-adventure',
}: TrainEditorProps) {
  const [draft, setDraft] = useState<DraftInput>(() => importedDraft ?? inputFromDraft(initialDraft, templateKey));
  const [mediaById, setMediaById] = useState<Record<string, DraftMedia>>(() => collectDraftMedia(initialDraft));
  const [saving, setSaving] = useState(false);
  const [uploadingTarget, setUploadingTarget] = useState('');
  const [message, setMessage] = useState('');
  const [openLevels, setOpenLevels] = useState<Set<number>>(() => new Set([1]));
  const [selectingBridgeMode, setSelectingBridgeMode] = useState(
    () => !initialDraft && templateKey === 'orbital-bridge',
  );
  const levelGroups = useMemo(
    () => Array.from({ length: draft.maxLevels }, (_, levelIndex) => ({
      level: levelIndex + 1,
      questions: questionsForLevel(draft, levelIndex + 1),
    })),
    [draft],
  );

  const updateQuestion = (questionIndex: number, update: Partial<DraftInput['questions'][number]>) => {
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((question, index) =>
        index === questionIndex ? { ...question, ...update } : question,
      ),
    }));
  };

  const updateChoice = (
    questionIndex: number,
    choiceIndex: number,
    update: Partial<DraftInput['questions'][number]['choices'][number]>,
  ) => {
    const question = draft.questions[questionIndex];
    const choices = question.choices.map((choice, index) => {
      if (update.isCorrect === true) {
        return { ...choice, isCorrect: index === choiceIndex };
      }
      return index === choiceIndex ? { ...choice, ...update } : choice;
    });
    updateQuestion(questionIndex, { choices });
  };

  const addChoice = (questionIndex: number) => {
    const question = draft.questions[questionIndex];
    const maximumChoices = ['billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? 4 : ['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena'].includes(draft.templateKey) ? 5 : 6;
    if (question.choices.length >= maximumChoices) return;
    updateQuestion(questionIndex, {
      choices: [...question.choices, { text: `Seçenek ${question.choices.length + 1}`, isCorrect: false }],
    });
  };

  const removeChoice = (questionIndex: number, choiceIndex: number) => {
    const question = draft.questions[questionIndex];
    if (question.choices.length <= 2 || draft.templateKey === 'billionaire-quiz') return;
    const choices = question.choices.filter((_choice, index) => index !== choiceIndex);
    if (!choices.some((choice) => choice.isCorrect)) choices[0] = { ...choices[0], isCorrect: true };
    updateQuestion(questionIndex, { choices });
  };

  const changeLevelCount = (nextMaxLevels: number) => {
    const removed = removedQuestionCount(draft, nextMaxLevels);
    if (removed > 0 && !window.confirm(
      `Seviye ${nextMaxLevels + 1}-${draft.maxLevels} ve bu seviyelerdeki ${removed} soru kaldırılacak. Devam etmek istiyor musun?`,
    )) return;

    const previousMaxLevels = draft.maxLevels;
    setDraft((current) => reconfigureDraft(current, nextMaxLevels));
    setOpenLevels((current) => {
      const next = new Set([...current].filter((level) => level <= nextMaxLevels));
      if (nextMaxLevels > previousMaxLevels) next.add(previousMaxLevels + 1);
      if (next.size === 0) next.add(1);
      return next;
    });
  };

  const toggleLevel = (level: number) => {
    setOpenLevels((current) => {
      const next = new Set(current);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  const uploadMedia = async (
    event: ChangeEvent<HTMLInputElement>,
    questionIndex: number,
    choiceIndex?: number,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (draft.templateKey === 'balance-arena') {
      if (choiceIndex === undefined) {
        const currentHasImage = Boolean(draft.questions[questionIndex]?.mediaId);
        const used = draft.questions.filter((question) => Boolean(question.mediaId)).length;
        if (!currentHasImage && used >= 10) {
          setMessage('Denge Arenası oyununda en fazla 10 soruya görsel ekleyebilirsiniz. Yeni bir soru görseli eklemek için önce mevcut soru görsellerinden birini kaldırın.');
          return;
        }
      } else {
        const currentHasImage = Boolean(draft.questions[questionIndex]?.choices[choiceIndex]?.mediaId);
        const used = draft.questions.reduce((total, question) => total + question.choices.filter((choice) => Boolean(choice.mediaId)).length, 0);
        if (!currentHasImage && used >= 10) {
          setMessage('Denge Arenası oyununda toplam en fazla 10 cevap seçeneğine görsel ekleyebilirsiniz. Yeni bir seçenek görseli eklemek için önce mevcut seçenek görsellerinden birini kaldırın.');
          return;
        }
      }
    }
    if (!mediaUploadAllowed) {
      setMessage(mediaUploadMessage ?? 'Yeni görsel yükleme şu anda kullanılamıyor.');
      return;
    }
    if (!initialDraft?.id) {
      setMessage('Görsel eklemek için önce taslağı kaydetmelisin.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setMessage('Yalnızca JPEG, PNG veya WebP görselleri yüklenebilir.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMessage('Görsel en fazla 2 MB olabilir.');
      return;
    }

    const target = choiceIndex === undefined
      ? `question-${questionIndex}`
      : `choice-${questionIndex}-${choiceIndex}`;
    setUploadingTarget(target);
    setMessage('Görsel güvenli biçime dönüştürülüyor…');
    try {
      const media = await uploadDraftMedia(initialDraft.id, file);
      setMediaById((current) => ({ ...current, [media.id]: media }));
      if (choiceIndex === undefined) updateQuestion(questionIndex, { mediaId: media.id, wagonStyle: 'wide' });
      else updateChoice(questionIndex, choiceIndex, { mediaId: media.id });
      setMessage('Görsel eklendi. Değişikliği kalıcı yapmak için taslağı kaydet.');
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Görsel yüklenemedi.');
    } finally {
      setUploadingTarget('');
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage('');
    const parsed = draftInputSchema.safeParse(draft);
    if (!parsed.success) {
      setMessage(parsed.error.issues[0]?.message ?? 'Taslak bilgilerini kontrol edin.');
      return;
    }
    setSaving(true);
    try {
      const saved = await saveDraft(parsed.data, initialDraft?.id);
      setMediaById(collectDraftMedia(saved));
      setMessage('Taslak güvenle kaydedildi.');
      onSaved(saved);
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Taslak kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const renderQuestionCard = (questionIndex: number, questionNumber: number) => {
    const question = draft.questions[questionIndex];
    return (
      <fieldset className="question-card" key={questionIndex}>
        <legend>{['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena'].includes(draft.templateKey) ? 'Soru içeriği' : `Soru ${questionNumber}`}</legend>
        <label>
          Soru metni <small className="field-hint">(görsel varsa isteğe bağlı)</small>
          <input
            value={question.prompt}
            maxLength={240}
            placeholder={question.mediaId ? 'İsteğe bağlı soru metni' : 'Soru metni veya aşağıdan görsel ekle'}
            onChange={(event) => updateQuestion(questionIndex, { prompt: event.target.value })}
          />
        </label>
        {draft.templateKey === 'train-adventure' && <fieldset className="wagon-style-picker">
          <legend>Vagon tasarımı</legend>
          <label className={question.wagonStyle !== 'narrow' ? 'selected' : ''}>
            <input
              type="radio"
              name={`wagon-style-${questionIndex}`}
              value="wide"
              checked={question.wagonStyle !== 'narrow'}
              onChange={() => updateQuestion(questionIndex, { wagonStyle: 'wide' })}
            />
            <span><strong>Geniş</strong><small>Görsel ve uzun metinler için</small></span>
          </label>
          <label className={question.wagonStyle === 'narrow' ? 'selected' : question.mediaId ? 'disabled' : ''}>
            <input
              type="radio"
              name={`wagon-style-${questionIndex}`}
              value="narrow"
              checked={question.wagonStyle === 'narrow'}
              disabled={Boolean(question.mediaId)}
              onChange={() => updateQuestion(questionIndex, { wagonStyle: 'narrow' })}
            />
            <span><strong>Dar</strong><small>Kısa, yalnızca metin içeren sorular için</small></span>
          </label>
          {question.mediaId && <p>Görsel içeren sorularda geniş vagon zorunludur.</p>}
        </fieldset>}
        <MediaControl
          label="Soru görseli"
          media={question.mediaId ? mediaById[question.mediaId] : undefined}
          disabled={!initialDraft?.id || Boolean(uploadingTarget) || !mediaUploadAllowed}
          uploading={uploadingTarget === `question-${questionIndex}`}
          onUpload={(event) => void uploadMedia(event, questionIndex)}
          onRemove={() => updateQuestion(questionIndex, { mediaId: null })}
        />
        {draft.templateKey === 'ufo-adventure' && draft.timingMode === 'per-question' && <label>
          Geçide yaklaşma süresi (saniye)
          <input type="number" min={10} max={120} value={question.timeLimitSeconds ?? 20}
            onChange={event => updateQuestion(questionIndex, { timeLimitSeconds: Number(event.target.value) })} />
          <small>10–120 saniye. Seçim yapılırsa UFO hızlanır; seçim yapılmazsa bulunduğu şeritten geçer.</small>
          <button type="button" className="small-action" onClick={() => setDraft(current => ({ ...current, questions: current.questions.map(q => ({ ...q, timeLimitSeconds: question.timeLimitSeconds ?? 20 })) }))}>Bu süreyi bütün sorulara uygula</button>
        </label>}
        {(draft.templateKey === 'orbital-bridge' || draft.templateKey === 'turbo-duel') && draft.timingMode === 'per-question' && (
          <label className="bridge-question-time">
            Bu soru için süre (saniye)
            <input
              type="number"
              min={5}
              max={600}
              value={question.timeLimitSeconds ?? 30}
              onChange={(event) => updateQuestion(questionIndex, {
                timeLimitSeconds: Math.max(5, Math.min(600, Number(event.target.value) || 5)),
              })}
            />
          </label>
        )}
        <div className="choice-list">
          {question.choices.map((choice, choiceIndex) => (
            <div className="choice-row" key={choiceIndex}>
              <input
                type="radio"
                name={`correct-${questionIndex}`}
                checked={choice.isCorrect}
                onChange={() => updateChoice(questionIndex, choiceIndex, { isCorrect: true })}
                aria-label={`${choiceIndex + 1}. seçeneği doğru cevap yap`}
              />
              <input
                value={choice.text}
                maxLength={100}
                placeholder={choice.mediaId ? 'İsteğe bağlı seçenek metni' : 'Seçenek metni veya görsel ekle'}
                onChange={(event) => updateChoice(questionIndex, choiceIndex, { text: event.target.value })}
                aria-label={`${choiceIndex + 1}. seçenek metni`}
              />
              <button type="button" onClick={() => removeChoice(questionIndex, choiceIndex)} disabled={question.choices.length <= 2 || draft.templateKey === 'billionaire-quiz'} aria-label={`${choiceIndex + 1}. seçeneği sil`}>×</button>
              <MediaControl
                compact
                label={`${choiceIndex + 1}. seçenek görseli`}
                media={choice.mediaId ? mediaById[choice.mediaId] : undefined}
                disabled={!initialDraft?.id || Boolean(uploadingTarget) || !mediaUploadAllowed}
                uploading={uploadingTarget === `choice-${questionIndex}-${choiceIndex}`}
                onUpload={(event) => void uploadMedia(event, questionIndex, choiceIndex)}
                onRemove={() => updateChoice(questionIndex, choiceIndex, { mediaId: null })}
              />
            </div>
          ))}
        </div>
        {draft.templateKey !== 'billionaire-quiz' && <button
          className="small-action"
          type="button"
          onClick={() => addChoice(questionIndex)}
          disabled={question.choices.length >= (['ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? 4 : ['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena'].includes(draft.templateKey) ? 5 : 6)}
        >+ Seçenek ekle</button>}
        {!initialDraft?.id && <p className="media-note">Görsel eklemek için önce taslağı kaydet.</p>}
        {initialDraft?.id && !mediaUploadAllowed && (
          <p className="media-note media-blocked" role="status">{mediaUploadMessage}</p>
        )}
      </fieldset>
    );
  };

  if (selectingBridgeMode) {
    return (
      <section className="editor-shell bridge-mode-shell" aria-labelledby="bridge-mode-title">
        <div className="editor-heading">
          <div>
            <p className="eyebrow">Yörünge Köprüsü</p>
            <h2 id="bridge-mode-title">Oyun modunu seç</h2>
            <p>Bu seçim öğrencinin yanlış cevap verdiğinde yolculuğun nasıl devam edeceğini belirler.</p>
          </div>
          <button className="ghost-button" type="button" onClick={onCancel}>Panele dön</button>
        </div>
        <div className="bridge-mode-grid">
          <button type="button" className="bridge-mode-card guided" onClick={() => {
            setDraft((current) => ({ ...current, bridgeMode: 'guided' }));
            setSelectingBridgeMode(false);
          }}>
            <span className="bridge-mode-icon" aria-hidden="true">✦</span>
            <strong>Rehberli Keşif</strong>
            <span>Her soru bir kez cevaplanır. Yanlışta doğru seçenek gösterilir, köprü tamamlanır ve yolculuk devam eder.</span>
            <small>Can yok · öğretici ve akıcı</small>
          </button>
          <button type="button" className="bridge-mode-card challenge" onClick={() => {
            setDraft((current) => ({ ...current, bridgeMode: 'challenge' }));
            setSelectingBridgeMode(false);
          }}>
            <span className="bridge-mode-icon" aria-hidden="true">◆</span>
            <strong>Üç Can Mücadelesi</strong>
            <span>Oyuncu doğru cevabı bulana kadar aynı soruda kalır. Yanlışlar ve zaman aşımı can götürür.</span>
            <small>3 can · puan kaybı · daha heyecanlı</small>
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="editor-shell" aria-labelledby="editor-title">
      <div className="editor-heading">
        <div>
          <p className="eyebrow">{templateName(draft.templateKey)} editörü</p>
          <h2 id="editor-title">{initialDraft ? 'Taslağı düzenle' : 'Yeni oyun hazırla'}</h2>
          <p>{draft.templateKey === 'station-run' ? '3 raylı koşu için 5–20 soru ve her soruda 2–4 seçenek hazırla. Öğrenci her 100 metrede bir istasyonda durur ve soruyu süre sınırı olmadan cevaplar. 4 can vardır: engele çarpmak 1 can götürür; yanlış cevapta 50 puan düşer, puan yetmezse 1 can gider. Altınlar ve doğru cevaplar puan kazandırır.' : draft.templateKey === 'lane-rally' ? '3B otoyol yarışı için 5–20 soru ve her soruda 2–4 seçenek hazırla. Her seçenek yolda bir şeride dönüşür; öğrenci gaz pedalıyla ilerler, doğru cevabın şeridine geçer. Soru süresi yoktur: gaz sürekli basılıysa yeni tabelaya 5 saniyede ulaşılır, öğrenci gazı bırakınca araba durur ve istediği kadar düşünür. Can yok; yanlışta doğru cevap gösterilir ve bütün sorular oynanır.' : draft.templateKey === 'ufo-adventure' ? 'Kesintisiz uzay yolculuğu için 5–20 soru ve her soruda 2–4 seçenek hazırla. Can yok; yanlışta doğru cevap gösterilir ve bütün sorular oynanır.' : draft.templateKey === 'train-adventure'
            ? 'Sorularını ve balonlarda görünecek seçenekleri hazırla. Taslak henüz yayımlanmaz.'
            : draft.templateKey === 'fiery-route'
              ? 'Her seviyenin sorusunu ve mozaik geçitlerde görünecek seçenekleri hazırla. Taslak henüz yayımlanmaz.'
              : draft.templateKey === 'orbital-bridge'
                ? 'Enerji modüllerinde görünecek cevapları ve yörünge yolculuğunun süre ayarlarını hazırla.'
                : draft.templateKey === 'turbo-duel'
                  ? 'Yarış sırasında görünecek soruları, cevap kartlarını ve istersen soru sürelerini hazırla.'
                  : draft.templateKey === 'balance-arena'
                    ? 'Gökyüzü arenasında cevaplanacak 5–25 soruyu hazırla. Denge ve rüzgâr ayarları bütün öğrenciler için otomatik ve eşittir.'
                    : draft.templateKey === 'billionaire-quiz'
                      ? 'Süresiz yarışma için 5–20 soru ve her soruda tam dört cevap hazırla. Para merdiveni ve bonus turları otomatik oluşturulur.'
                      : draft.templateKey === 'goal-arena'
                        ? 'Birinci şahıs futbol finalinde şuta dönüşecek 4–20 soruyu ve 2–5 cevap seçeneğini hazırla.'
                        : 'LED ekranlı basketbol arenasında potaya gönderilecek 4–20 soruyu ve 2–5 cevap seçeneğini hazırla.'}</p>
          {importedDraft && !initialDraft && <p className="import-banner" role="status">
            📄 Dosyadan {importedDraft.questions.length} soru aktarıldı. Soruları kontrol et, istersen görsel ekle ve <strong>Taslağı kaydet</strong> düğmesine bas; kaydetmeden taslak oluşmaz.
          </p>}
          <p className="media-authority-note">
            Yüklediğin görselleri kullanma ve öğrencilerle paylaşma yetkisine sahip olduğundan emin ol; kişisel öğrenci fotoğraflarını gerekli izin olmadan yükleme.
          </p>
        </div>
        <button className="ghost-button" type="button" onClick={onCancel}>Panele dön</button>
      </div>

      <form className="editor-form" onSubmit={submit}>
        <fieldset className="editor-settings">
          <legend>Oyun ayarları</legend>
          <label className="wide-field">
            Etkinlik başlığı
            <input value={draft.title} maxLength={100} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
          </label>
          <label>
            {['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena', 'billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? 'Soru sayısı' : 'Seviye sayısı'}
            <select
              value={draft.maxLevels}
              onChange={(event) => changeLevelCount(Number(event.target.value))}
            >
              {Array.from(
                { length: draft.templateKey === 'train-adventure' ? 5 : draft.templateKey === 'balance-arena' ? 21 : ['billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? 16 : ['basket-learning', 'goal-arena'].includes(draft.templateKey) ? 17 : 10 },
                (_, index) => index + (['balance-arena', 'billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? 5 : ['basket-learning', 'goal-arena'].includes(draft.templateKey) ? 4 : 1),
              ).map((level) => <option value={level} key={level}>{level} {['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena', 'billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? 'soru' : 'seviye'}</option>)}
            </select>
          </label>
          {draft.templateKey === 'ufo-adventure' && <fieldset className="bridge-inline-mode wide-field ufo-mode-settings">
            <legend>UFO geçide ne zaman girsin?</legend>
            <label className={draft.timingMode === 'per-question' ? 'selected' : ''}>
              <input type="radio" name="ufo-timing" checked={draft.timingMode === 'per-question'} onChange={() => setDraft(setUfoTiming(draft, 'per-question'))} />
              <span><strong>Otomatik geçiş</strong><small>UFO geçitlere kendiliğinden yaklaşır. Öğrenci seçim yapmazsa bulunduğu şeritteki cevap kullanılır. Bir seçenek seçerse UFO o geçide yönelip hızlanır.</small></span>
            </label>
            <label className={draft.timingMode === 'none' ? 'selected' : ''}>
              <input type="radio" name="ufo-timing" checked={draft.timingMode === 'none'} onChange={() => setDraft(setUfoTiming(draft, 'none'))} />
              <span><strong>Seçim yapana kadar bekle</strong><small>UFO uçmaya devam eder; cevap geçitleri uzakta kalır. Öğrenci bir seçenek seçince UFO o geçide yaklaşır. Seçim için süre sınırı yoktur.</small></span>
            </label>
          </fieldset>}
          {draft.templateKey === 'fiery-route' && <fieldset className="bridge-inline-mode wide-field ufo-mode-settings">
            <legend>Oyun modu</legend>
            <label className={draft.bridgeMode === 'guided' ? 'selected' : ''}>
              <input type="radio" name="route-mode" checked={draft.bridgeMode === 'guided'} onChange={() => setDraft({ ...draft, bridgeMode: 'guided' })} />
              <span><strong>Normal mod</strong><small>Can yok. Yanlış kapıya girilirse üzerinde ✕ çıkar, kamera doğru kapıya yaklaşıp doğru cevabı gösterir ve sıradaki soruya geçilir. Öğrenci bütün soruları görür.</small></span>
            </label>
            <label className={draft.bridgeMode !== 'guided' ? 'selected' : ''}>
              <input type="radio" name="route-mode" checked={draft.bridgeMode !== 'guided'} onChange={() => setDraft({ ...draft, bridgeMode: 'challenge' })} />
              <span><strong>3 Can modu</strong><small>Yanlış kapı ve Çiselere yakalanmak bir can götürür; soru doğru kapı bulunana kadar sürer. Canlar biterse oyun biter.</small></span>
            </label>
          </fieldset>}
          {draft.templateKey === 'balance-arena' && <fieldset className="bridge-inline-mode wide-field ufo-mode-settings">
            <legend>Sorular bitince ne olsun?</legend>
            <label className={!draft.repeatUntilFall ? 'selected' : ''}>
              <input type="radio" name="balance-repeat" checked={!draft.repeatUntilFall} onChange={() => setDraft({ ...draft, repeatUntilFall: undefined })} />
              <span><strong>Oyun bitsin</strong><small>Bütün sorular bir kez sorulur. Öğrenci düşmeden son soruya ulaşırsa oyunu tamamlar.</small></span>
            </label>
            <label className={draft.repeatUntilFall ? 'selected' : ''}>
              <input type="radio" name="balance-repeat" checked={Boolean(draft.repeatUntilFall)} onChange={() => setDraft({ ...draft, repeatUntilFall: true })} />
              <span><strong>Düşene kadar tekrar sorulsun</strong><small>Sorular bitince aynı sorular karışık sırayla yeniden gelir; her yeni turda rüzgâr biraz güçlenir. Oyun öğrenci düşünce ya da 5 dakika dolunca biter. Çarpım tablosu gibi kısa, hızlı tekrar soruları için idealdir; doğru sayısı ve dayanma süresi birlikte önemlidir.</small></span>
            </label>
          </fieldset>}
          {draft.templateKey === 'train-adventure' && <label className="check-field wide-field">
            <input type="checkbox" checked={draft.trackTime} onChange={(event) => setDraft({ ...draft, trackTime: event.target.checked })} />
            Oyuncunun toplam tamamlama süresini ölç
          </label>}
          {draft.templateKey === 'orbital-bridge' && <>
            <fieldset className="bridge-inline-mode wide-field">
              <legend>Oyun modu</legend>
              <label className={draft.bridgeMode === 'guided' ? 'selected' : ''}>
                <input type="radio" name="bridge-mode" checked={draft.bridgeMode === 'guided'} onChange={() => setDraft({ ...draft, bridgeMode: 'guided' })} />
                <span><strong>Rehberli Keşif</strong><small>Yanlışta doğruyu gösterip ilerler.</small></span>
              </label>
              <label className={draft.bridgeMode === 'challenge' ? 'selected' : ''}>
                <input type="radio" name="bridge-mode" checked={draft.bridgeMode === 'challenge'} onChange={() => setDraft({ ...draft, bridgeMode: 'challenge' })} />
                <span><strong>Üç Can Mücadelesi</strong><small>Doğru bulunana kadar soru sürer.</small></span>
              </label>
            </fieldset>
            <fieldset className="bridge-inline-mode wide-field">
              <legend>Süre ayarı</legend>
              <label className={draft.timingMode === 'none' ? 'selected' : ''}>
                <input type="radio" name="bridge-timing" checked={draft.timingMode === 'none'} onChange={() => setDraft({ ...draft, timingMode: 'none' })} />
                <span><strong>Süre sınırı yok</strong><small>Toplam ve soru süreleri yine ölçülür.</small></span>
              </label>
              <label className={draft.timingMode === 'per-question' ? 'selected' : ''}>
                <input type="radio" name="bridge-timing" checked={draft.timingMode === 'per-question'} onChange={() => setDraft({
                  ...draft,
                  timingMode: 'per-question',
                  questions: draft.questions.map((question) => ({ ...question, timeLimitSeconds: question.timeLimitSeconds ?? 30 })),
                })} />
                <span><strong>Soru başına süre</strong><small>Her sorunun süresini ayrı gir.</small></span>
              </label>
            </fieldset>
          </>}
          {draft.templateKey === 'turbo-duel' && (
            <fieldset className="bridge-inline-mode wide-field">
              <legend>Süre ayarı</legend>
              <label className={draft.timingMode === 'none' ? 'selected' : ''}>
                <input type="radio" name="race-timing" checked={draft.timingMode === 'none'} onChange={() => setDraft({ ...draft, timingMode: 'none' })} />
                <span><strong>Süre sınırı yok</strong><small>Toplam ve soru süreleri raporda yine ölçülür.</small></span>
              </label>
              <label className={draft.timingMode === 'per-question' ? 'selected' : ''}>
                <input type="radio" name="race-timing" checked={draft.timingMode === 'per-question'} onChange={() => setDraft({
                  ...draft,
                  timingMode: 'per-question',
                  questions: draft.questions.map((question) => ({ ...question, timeLimitSeconds: question.timeLimitSeconds ?? 30 })),
                })} />
                <span><strong>Soru başına süre</strong><small>Süre dolarsa cevap yanlış sayılır ve yarış devam eder.</small></span>
              </label>
            </fieldset>
          )}
        </fieldset>

        <div className="level-accordion-list">
          {levelGroups.map(({ level, questions }) => {
            const isOpen = openLevels.has(level);
            const readyCount = questions.filter(({ question }) => isQuestionReady(question)).length;
            return (
              <section className={`level-accordion${isOpen ? ' open' : ''}`} key={level}>
                <button
                  className="level-accordion-trigger"
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`level-${level}-questions`}
                  onClick={() => toggleLevel(level)}
                >
                  <span className="level-accordion-icon" aria-hidden="true">⌄</span>
                  <strong>{['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena', 'billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? `Soru ${level}` : `Seviye ${level}`}</strong>
                  <span>{['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena', 'billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? (readyCount ? 'Hazır' : 'Eksik') : `${readyCount}/${questions.length} soru hazır`}</span>
                </button>
                {isOpen && (
                  <div className="level-question-list" id={`level-${level}-questions`}>
                    {questions.map(({ questionIndex }, index) => renderQuestionCard(questionIndex, index + 1))}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <div className="editor-actions">
          {message && <p className="editor-message" role="status">{message}</p>}
          <button className="primary-button" type="submit" disabled={saving}>{saving ? 'Kaydediliyor…' : 'Taslağı kaydet'}</button>
        </div>
      </form>
    </section>
  );
}

type MediaControlProps = {
  label: string;
  media?: DraftMedia;
  disabled: boolean;
  uploading: boolean;
  compact?: boolean;
  onUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
};

function MediaControl({ label, media, disabled, uploading, compact, onUpload, onRemove }: MediaControlProps) {
  return (
    <div className={`media-control${compact ? ' media-control-compact' : ''}`}>
      {media && <img src={media.url} alt="" className="media-thumbnail" />}
      <div className="media-control-actions">
        <span>{label}</span>
        <label className={`media-upload${disabled ? ' disabled' : ''}`}>
          {uploading ? 'Yükleniyor…' : media ? 'Değiştir' : 'Görsel ekle'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={disabled}
            onChange={onUpload}
          />
        </label>
        {media && <button type="button" className="media-remove" onClick={onRemove}>Kaldır</button>}
      </div>
    </div>
  );
}

function collectDraftMedia(draft?: GameDraft): Record<string, DraftMedia> {
  const entries = draft?.questions.flatMap((question) => [
    ...(question.media ? [[question.media.id, question.media] as const] : []),
    ...question.choices.flatMap((choice) => choice.media
      ? [[choice.media.id, choice.media] as const]
      : []),
  ]) ?? [];
  return Object.fromEntries(entries);
}
